import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import type { WorkOrder, PaginatedResult, User, Client, ClientSite, ScheduleTemplate } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Drawer } from '../components/ui/Drawer'
import { GlowCard } from '../components/ui/GlowCard'
import { Button } from '../components/ui/Button'
import { Select } from '../components/ui/Select'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'var(--err)',
  high: 'var(--accent)',
  normal: 'var(--info)',
  low: 'var(--text-3)',
}

function formatTime(t: string | null) {
  if (!t) return ''
  try {
    const d = new Date(t)
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  } catch {
    return t
  }
}

function formatDate(d: string | null) {
  if (!d) return ''
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return d
  }
}

export default function Dispatch() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const view = searchParams.get('view') || 'today'

  const [todayOrders, setTodayOrders] = useState<WorkOrder[]>([])
  const [unscheduled, setUnscheduled] = useState<WorkOrder[]>([])
  const [calendarOrders, setCalendarOrders] = useState<WorkOrder[]>([])
  const [allOrders, setAllOrders] = useState<WorkOrder[]>([])
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [typeFilter, setTypeFilter] = useState('')

  // Create form state
  const [drawerOpen, setDrawerOpen] = useState(searchParams.get('action') === 'new')
  const [saving, setSaving] = useState(false)
  const [clients, setClients] = useState<{ value: string; label: string }[]>([])
  const [sites, setSites] = useState<ClientSite[]>([])
  const [users, setUsers] = useState<{ value: string; label: string }[]>([])
  const [templates, setTemplates] = useState<ScheduleTemplate[]>([])
  const [formData, setFormData] = useState({
    type: 'service' as string,
    priority: 'normal' as string,
    title: '',
    description: '',
    client_id: '',
    site_id: '',
    assigned_to: '',
    scheduled_date: '',
    scheduled_start: '',
    scheduled_end: '',
    template_id: '',
  })

  // Calendar state
  const [calendarDate, setCalendarDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - d.getDay() + 1) // Start on Monday
    return d.toISOString().split('T')[0]
  })

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      if (view === 'today') {
        const [today, unsched] = await Promise.all([
          api.get<WorkOrder[]>('/dispatch/today').catch(() => []),
          api.get<WorkOrder[]>('/dispatch/unscheduled').catch(() => []),
        ])
        setTodayOrders(Array.isArray(today) ? today : [])
        setUnscheduled(Array.isArray(unsched) ? unsched : [])
      } else if (view === 'calendar') {
        const from = calendarDate
        const to = new Date(new Date(calendarDate).getTime() + 6 * 86400000).toISOString().split('T')[0]
        const data = await api.get<WorkOrder[]>(`/dispatch/calendar?date_from=${from}&date_to=${to}`).catch(() => [])
        setCalendarOrders(Array.isArray(data) ? data : [])
      } else {
        const params = new URLSearchParams({ page: String(page), limit: '20' })
        if (typeFilter) params.set('type', typeFilter)
        const result = await api.get<PaginatedResult<WorkOrder>>(`/work-orders?${params}`)
        setAllOrders(result.data || [])
        setTotalPages(result.totalPages || 1)
      }
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [view, page, typeFilter, calendarDate])

  useEffect(() => { loadData() }, [loadData])

  // Load form dropdown data when drawer opens
  useEffect(() => {
    if (!drawerOpen) return
    Promise.all([
      api.get<PaginatedResult<Client>>('/clients?limit=100').then(r =>
        setClients((r.data || []).map(c => ({ value: c.id, label: c.company_name })))
      ),
      api.get<User[]>('/users').then(r =>
        setUsers((Array.isArray(r) ? r : []).map(u => ({ value: u.id, label: u.full_name })))
      ),
      api.get<ScheduleTemplate[]>('/schedule-templates').then(r => setTemplates(Array.isArray(r) ? r : [])),
    ]).catch(() => {})
  }, [drawerOpen])

  // Load sites when client changes
  useEffect(() => {
    if (!formData.client_id) { setSites([]); return }
    api.get<PaginatedResult<{ id: string; slug: string; name: string; address: string | null; city: string | null; state: string | null }>>(`/clients/${formData.client_id}/sites?limit=100`)
      .then(r => setSites(r.data as ClientSite[]))
      .catch(() => setSites([]))
  }, [formData.client_id])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        type: formData.type,
        priority: formData.priority,
        title: formData.title,
        description: formData.description || undefined,
        client_id: formData.client_id || undefined,
        site_id: formData.site_id || undefined,
        assigned_to: formData.assigned_to || undefined,
        scheduled_date: formData.scheduled_date || undefined,
        scheduled_start: formData.scheduled_start ? `${formData.scheduled_date}T${formData.scheduled_start}` : undefined,
        scheduled_end: formData.scheduled_end ? `${formData.scheduled_date}T${formData.scheduled_end}` : undefined,
        template_id: formData.template_id || undefined,
      }
      const result = await api.post<{ slug?: string }>('/work-orders', payload)
      setDrawerOpen(false)
      setFormData({ type: 'service', priority: 'normal', title: '', description: '', client_id: '', site_id: '', assigned_to: '', scheduled_date: '', scheduled_start: '', scheduled_end: '', template_id: '' })
      if (result?.slug) {
        navigate(`/dispatch/${result.slug}`)
      } else {
        loadData()
      }
    } catch { /* error shown */ }
    finally { setSaving(false) }
  }

  function WorkOrderCard({ wo }: { wo: WorkOrder }) {
    const location = [wo.site_address, wo.site_city, wo.site_state].filter(Boolean).join(', ')
    return (
      <GlowCard
        onClick={() => navigate(`/dispatch/${wo.slug}`)}
        style={{
          padding: 14,
          borderLeft: `4px solid ${PRIORITY_COLORS[wo.priority] || 'var(--text-3)'}`,
        }}
        delay={100}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              {wo.scheduled_start && (
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
                  {formatTime(wo.scheduled_start)}
                </span>
              )}
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {wo.title}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-2)' }}>
              {wo.assigned_name && <span>Assigned: {wo.assigned_name}</span>}
              {wo.assigned_name && wo.type && <span>&middot;</span>}
              <span style={{ textTransform: 'capitalize' }}>{wo.type}</span>
              <span>&middot;</span>
              <span style={{ textTransform: 'capitalize' }}>{wo.priority}</span>
            </div>
            {(wo.client_name || location) && (
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
                {wo.client_name}{location ? ` — ${location}` : ''}
              </div>
            )}
          </div>
          <StatusBadge status={wo.status} />
        </div>
      </GlowCard>
    )
  }

  function renderTodayView() {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>
            Today &middot; {today}
          </h2>
          {todayOrders.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-3)', padding: '16px 0' }}>No work orders scheduled for today.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {todayOrders.map(wo => <WorkOrderCard key={wo.id} wo={wo} />)}
            </div>
          )}
        </div>

        {unscheduled.length > 0 && (
          <div>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>
              Unscheduled ({unscheduled.length})
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {unscheduled.map(wo => <WorkOrderCard key={wo.id} wo={wo} />)}
            </div>
          </div>
        )}
      </div>
    )
  }

  function renderCalendarView() {
    const weekStart = new Date(calendarDate + 'T00:00:00')
    const days: { date: string; label: string; dayLabel: string }[] = []
    for (let i = 0; i < 5; i++) {
      const d = new Date(weekStart.getTime() + i * 86400000)
      days.push({
        date: d.toISOString().split('T')[0],
        label: d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
        dayLabel: d.toLocaleDateString('en-US', { weekday: 'short' }),
      })
    }

    const ordersByDate: Record<string, WorkOrder[]> = {}
    for (const wo of calendarOrders) {
      if (!wo.scheduled_date) continue
      if (!ordersByDate[wo.scheduled_date]) ordersByDate[wo.scheduled_date] = []
      ordersByDate[wo.scheduled_date].push(wo)
    }

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const prev = new Date(new Date(calendarDate).getTime() - 7 * 86400000)
              setCalendarDate(prev.toISOString().split('T')[0])
            }}
          >
            &larr; Prev
          </Button>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>
            {formatDate(days[0]?.date)} — {formatDate(days[4]?.date)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const next = new Date(new Date(calendarDate).getTime() + 7 * 86400000)
              setCalendarDate(next.toISOString().split('T')[0])
            }}
          >
            Next &rarr;
          </Button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, minHeight: 300 }}>
          {days.map(day => (
            <div key={day.date} style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-m)', padding: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 8 }}>
                {day.dayLabel} {day.label}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(ordersByDate[day.date] || []).map(wo => (
                  <div
                    key={wo.id}
                    onClick={() => navigate(`/dispatch/${wo.slug}`)}
                    style={{
                      padding: '6px 8px',
                      borderRadius: 6,
                      background: 'var(--bg-2)',
                      borderLeft: `3px solid ${PRIORITY_COLORS[wo.priority] || 'var(--text-3)'}`,
                      cursor: 'pointer',
                      transition: 'opacity var(--dur-f) var(--ease)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.8' }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
                  >
                    {wo.scheduled_start && (
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
                        {formatTime(wo.scheduled_start)}
                      </div>
                    )}
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-1)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {wo.title}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {wo.client_name || wo.assigned_name || ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  function renderWeekView() {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <Select
            options={[
              { value: '', label: 'All Types' },
              { value: 'service', label: 'Service' },
              { value: 'install', label: 'Install' },
              { value: 'maintenance', label: 'Maintenance' },
              { value: 'inspection', label: 'Inspection' },
              { value: 'warranty', label: 'Warranty' },
            ]}
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
          />
        </div>

        {allOrders.length === 0 ? (
          <EmptyState
            title="No work orders"
            description="Create your first work order."
            action={<Button onClick={() => setDrawerOpen(true)}>New Work Order</Button>}
          />
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {allOrders.map(wo => <WorkOrderCard key={wo.id} wo={wo} />)}
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{page} / {totalPages}</span>
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto', animation: 'in-up 400ms var(--ease) both' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-1)', margin: 0 }}>Dispatch</h1>
        <Button icon={PlusIcon} onClick={() => setDrawerOpen(true)}>New Work Order</Button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'today', label: 'Today' },
          { key: 'week', label: 'This Week' },
          { key: 'calendar', label: 'Calendar' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setSearchParams(tab.key === 'today' ? {} : { view: tab.key }); setPage(1) }}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: view === tab.key ? 600 : 400,
              color: view === tab.key ? 'var(--amber)' : 'var(--text-2)',
              background: view === tab.key ? 'var(--accent-m)' : 'transparent',
              border: `1px solid ${view === tab.key ? 'var(--amber)' : 'var(--border)'}`,
              borderRadius: 'var(--r-m)',
              cursor: 'pointer',
              transition: 'all var(--dur-f) var(--ease)',
              fontFamily: 'var(--font)',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <TableSkeleton />
      ) : (
        <>
          {view === 'today' && renderTodayView()}
          {view === 'calendar' && renderCalendarView()}
          {view === 'week' && renderWeekView()}
        </>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => { setDrawerOpen(false); if (searchParams.get('action')) setSearchParams(prev => { prev.delete('action'); return prev }, { replace: true }) }} title="New Work Order">
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Type *</label>
              <Select
                options={[
                  { value: 'service', label: 'Service' },
                  { value: 'install', label: 'Install' },
                  { value: 'maintenance', label: 'Maintenance' },
                  { value: 'inspection', label: 'Inspection' },
                  { value: 'warranty', label: 'Warranty' },
                ]}
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Priority</label>
              <Select
                options={[
                  { value: 'low', label: 'Low' },
                  { value: 'normal', label: 'Normal' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' },
                ]}
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="v3-input"
              placeholder="Camera offline — Goldstein Residence"
              required
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="v3-textarea"
              rows={3}
              placeholder="Detailed issue description..."
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Client</label>
            <Select
              options={[{ value: '', label: 'Select client...' }, ...clients]}
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: e.target.value, site_id: '' })}
            />
          </div>
          {sites.length > 0 && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Site</label>
              <Select
                options={[{ value: '', label: 'Select site...' }, ...sites.map(s => ({ value: s.id, label: s.name }))]}
                value={formData.site_id}
                onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
              />
            </div>
          )}
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Assign To</label>
            <Select
              options={[{ value: '', label: 'Unassigned' }, ...users]}
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Schedule</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <input type="date" value={formData.scheduled_date} onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })} className="v3-input" />
              <input type="time" value={formData.scheduled_start} onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })} className="v3-input" placeholder="Start" />
              <input type="time" value={formData.scheduled_end} onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value })} className="v3-input" placeholder="End" />
            </div>
          </div>
          {templates.length > 0 && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Checklist Template</label>
              <Select
                options={[{ value: '', label: 'None' }, ...templates.map(t => ({ value: t.id, label: t.name }))]}
                value={formData.template_id}
                onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
              />
            </div>
          )}
          <Button type="submit" loading={saving} style={{ width: '100%' }}>
            Create Work Order
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
